import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ShieldCheck, Plus, Minus, Sparkles, X, Mail, Droplets, CalendarClock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { usePlanDiscounts } from "@/features/customer-360/serviceTypes";
import { repairPctForTerm, useRenewalStart, usePlanLeftovers, useSellPlan, createPlanInvoice } from "./planPricing";
import { usePlanVisitCredit, fmtShort } from "./planCredit";
import { normalizeTemplate, useScheduleTuneup, type TuneupTemplateItem, type TuneupDueRow } from "./obligations";
import { useNavigate } from "react-router-dom";
import { Receipt } from "lucide-react";
import { searchPricebook as searchBook } from "@/features/items/pricebookSearch";
// MEM-SALE-02 (Seth 2026-10-06: "No where in the build & price was I able to
// select equipment"): the sale flow now shows the home itself — covered
// equipment (same picker as Manage plan) and the humidifier, auto-detected
// from the equipment records with a confirm toggle + count. Pads ride along
// free; the count is a record, never a price.
import { CoveredEquipment } from "@/features/customer-360/ManagePlanModal";
import { useCustomerEquipment } from "@/features/customer-360/CustomerEquipment";
import { usePlanAccessories } from "@/features/customer-360/PlanAccessoriesEditor";
import { classifyEquipment } from "@/lib/equipment";
import { humidifierPad } from "@/lib/truckParts";

// ============================================================================
// PlanBuilder — the office sells / renews a PLUS plan in one snappy modal:
// pick a term, a base plan, add-ons and à-la-carte items; the price recomputes
// live off the editable pegasus_plan_price catalog (Settings → Plan Pricing).
// "Select equipment, select accessories, pricing fluctuates" (Seth).
// ============================================================================

export interface PlanPrice {
  id: string; kind: "base" | "addon" | "item"; name: string; sort: number;
  p1: number | null; p2: number | null; p3: number | null; p5: number | null; p10: number | null;
  flat: number | null; active: boolean;
  tuneups?: TuneupTemplateItem[] | null; // base plans only — the tune-up template (null = default)
}
interface PricebookItem { id: string; part_number: string | null; description: string | null; base_price: number | null }
// Humidifier pads / water panels (APR10/APR12/APR35, etc.) are INCLUDED in the
// PLUS base plans at no charge — list them as covered, never priced as a part.
const isIncludedPad = (q: string) => /\bapr\s?-?\s?(10|12|35)\b|humidifier\s*pad|water\s*panel|evap(orator)?\s*pad/i.test(q);
type Term = 1 | 2 | 3 | 5 | 10;
const TERM_KEY: Record<Term, "p1" | "p2" | "p3" | "p5" | "p10"> = { 1: "p1", 2: "p2", 3: "p3", 5: "p5", 10: "p10" };
const TERM_LABEL: Record<Term, string> = { 1: "1 Year", 2: "2 Year", 3: "3 Year", 5: "5 Year", 10: "10 Year" };

export function usePlanCatalog() {
  return useQuery({
    queryKey: ["plan_catalog"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [{ data: prices }, { data: config }] = await Promise.all([
        supabase.from("pegasus_plan_price").select("*").eq("active", true).order("sort"),
        supabase.from("pegasus_plan_config").select("key, value"),
      ]);
      const cfg: Record<string, unknown> = {};
      for (const c of (config ?? []) as { key: string; value: unknown }[]) cfg[c.key] = c.value;
      return { prices: (prices ?? []) as PlanPrice[], config: cfg };
    },
  });
}

const money = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD" });
const pct = (v: number) => `${Math.round(v * 100)}%`;
const fmtDate = (iso: string | null) => { if (!iso) return "—"; const [y, m, d] = iso.split("-"); return `${m}/${d}/${y}`; };

// The welcome receipt a new member gets when a plan is sold. Customer-facing, so
// strictly "Sedgwick Heating" (never "Pegasus"). Transactional. Matches the
// portal-link email's brand (navy #16314f, red CTA). The send is comms-gated —
// nothing reaches a real customer until org_setting comms_live = true.
function welcomeEmail(o: {
  name: string | null; planName: string; termLabel: string;
  start: string; expire: string; covered: string[]; repairPct: number; tripCharge: number;
}) {
  const LOGO = "https://trfusfityhwecvglnfhm.supabase.co/storage/v1/object/public/email-assets/sedgwick-logo.png";
  const coveredRows = o.covered
    .map((c) => `<tr><td style="padding:5px 0;color:#16314f;font-size:14px;border-bottom:1px solid #eef0f3">&#10003;&nbsp;&nbsp;${c}</td></tr>`)
    .join("");
  const benefit = (t: string) => `<tr><td style="padding:5px 0;color:#2f7d4f;font-size:14px;font-weight:600">&#9733;&nbsp;&nbsp;${t}</td></tr>`;
  const benefits = [
    o.repairPct > 0 ? `${Math.round(o.repairPct * 100)}% off repairs` : "Member repair discount",
    `$${o.tripCharge.toFixed(0)} trip charge`,
    "Priority scheduling, ahead of non-members",
  ].map(benefit).join("");
  return `<div style="max-width:500px;margin:0 auto;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#ffffff;border-radius:14px;overflow:hidden;border:1px solid #e8eaed">
    <div style="text-align:center;padding:26px 24px 20px;border-bottom:3px solid #16314f"><img src="${LOGO}" alt="Sedgwick Heating" style="width:180px;max-width:60%"/></div>
    <div style="padding:30px 34px">
      <h1 style="font-size:21px;color:#16314f;margin:0 0 6px">Welcome${o.name ? `, ${o.name}` : ""}!</h1>
      <p style="color:#5f6b7a;font-size:15px;line-height:1.6;margin:0 0 22px">You're now a Sedgwick Heating Plus member. Here's what your plan covers and the membership perks that come with it.</p>
      <div style="background:#f6f8fb;border-radius:11px;padding:18px 20px;margin:0 0 22px">
        <div style="font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#9aa4b1;font-weight:700;margin:0 0 4px">Your plan</div>
        <div style="font-size:17px;color:#16314f;font-weight:700">${o.planName}</div>
        <div style="font-size:13px;color:#5f6b7a;margin:3px 0 0">${o.termLabel} &middot; covers ${fmtDate(o.start)} through ${fmtDate(o.expire)}</div>
      </div>
      <div style="font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#9aa4b1;font-weight:700;margin:0 0 6px">What's covered</div>
      <table style="width:100%;border-collapse:collapse;margin:0 0 22px">${coveredRows}</table>
      <div style="font-size:12px;text-transform:uppercase;letter-spacing:.05em;color:#9aa4b1;font-weight:700;margin:0 0 6px">Your member perks</div>
      <table style="width:100%;border-collapse:collapse;margin:0 0 24px">${benefits}</table>
      <div style="text-align:center"><a href="https://my.sedgwickheating.com" style="display:inline-block;background:#c0392b;color:#fff;text-decoration:none;padding:13px 32px;border-radius:9px;font-weight:700;font-size:15px">Manage my plan online</a></div>
      <p style="text-align:center;margin:13px 0 0;font-size:13px"><a href="https://www.sedgwickheating.com/service-plans/" style="color:#1f5fa8;text-decoration:none;font-weight:600">See everything your plan includes &rarr;</a></p>
      <p style="color:#9aa4b1;font-size:13px;line-height:1.6;margin:22px 0 0">We'll reach out to schedule your included tune-ups &mdash; no need to remember. Questions? Call <a href="tel:9528819000" style="color:#1f5fa8;font-weight:700">(952) 881-9000</a>.</p>
    </div>
    <div style="text-align:center;font-size:12px;color:#9aa4b1;padding:0 0 22px">Sedgwick Heating &amp; Air Conditioning &middot; Eagan, MN</div>
  </div>`;
}

export function PlanBuilderModal({ custno, locno, onClose }: { custno: string; locno?: string | null; onClose: () => void }) {
  const { data, isLoading } = usePlanCatalog();
  const { data: discountTiers } = usePlanDiscounts();
  const qc = useQueryClient();
  const prices = data?.prices ?? [];
  const config = data?.config ?? {};
  const terms = ((config.terms as number[]) ?? [1, 2, 3, 5, 10]) as Term[];
  const secondDisc = Number(config.second_system_discount ?? 0.3);
  const tripCharge = Number(config.trip_charge ?? 79);
  const repairDisc = (config.repair_discount as Record<string, number>) ?? {};

  const bases = prices.filter((p) => p.kind === "base");
  const addons = prices.filter((p) => p.kind === "addon");
  const items = prices.filter((p) => p.kind === "item");

  const [term, setTerm] = useState<Term>(3);
  const [baseId, setBaseId] = useState<string>("");
  const [addonIds, setAddonIds] = useState<Set<string>>(new Set());
  const [secondSystem, setSecondSystem] = useState(false);
  const [itemQty, setItemQty] = useState<Record<string, number>>({});
  const [saved, setSaved] = useState(false);
  const [soldMode, setSoldMode] = useState(false);
  // BUILDER-REDESIGN (Seth 2026-08-18: "completely busy and packed and
  // complex and small... complete relook"): the one-screen wall becomes three
  // roomy steps — Plan, Extras, Review — Run a Call's tier-card + step-sell
  // pattern. The running total stays pinned top-right the whole way.
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [aiOpen, setAiOpen] = useState(false);
  const [welcomeMsg, setWelcomeMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  // MEMBERSHIPS-02 P1: the purchase → term rule. A renewal starts where the
  // current plan ends (before expiry, or inside the grace window); a new
  // member starts today. Pre-filled from the rule; the office can still pick
  // another date (sold with an install, say).
  const { data: renewal } = useRenewalStart(custno);
  // MEMBERSHIPS-03 rule 4a: leftover checks from the old term — folded by
  // default, said out loud here, carried over with one click.
  const { data: leftovers } = usePlanLeftovers(custno, startDate);
  const [carryOver, setCarryOver] = useState(false);
  const [startTouched, setStartTouched] = useState(false);
  useEffect(() => {
    if (renewal && !startTouched) setStartDate(renewal.start_date);
  }, [renewal, startTouched]);
  // MEM-SALE-02 — the humidifier is a RECORD with a count, never a price
  // (Seth 2026-10-06: "we need to know if there is a humidifier (or how many)
  // that we are servicing and that the pad is included"). Auto-detected from
  // the home's equipment records, confirmed by the seller.
  const { data: homeEquip } = useCustomerEquipment(custno, locno ?? null);
  const { data: existingAccessories } = usePlanAccessories(custno, locno ?? null);
  const detectedHumis = useMemo(
    () => (homeEquip ?? []).filter((r) => classifyEquipment(r).category === "humidifier"),
    [homeEquip],
  );
  const detectedPad = useMemo(() => {
    for (const r of detectedHumis) {
      const p = humidifierPad((r as { model?: string | null }).model);
      if (p) return p;
    }
    return null;
  }, [detectedHumis]);
  const [humiTouched, setHumiTouched] = useState(false);
  const [humiOn, setHumiOn] = useState(false);
  const [humiCount, setHumiCount] = useState(1);
  useEffect(() => {
    if (humiTouched || detectedHumis.length === 0) return;
    setHumiOn(true);
    setHumiCount(detectedHumis.length);
  }, [detectedHumis, humiTouched]);
  // The sold plan's id — the post-sale panel books its first visit from it.
  const [saleId, setSaleId] = useState<string | null>(null);
  // PLAN-SALE-INVOICE: the plan's own invoice — Collect lives there; payment activates the plan.
  const [saleInvoiceId, setSaleInvoiceId] = useState<string | null>(null);
  const sellPlan = useSellPlan();
  const navigate = useNavigate();
  // Custom one-off lines — anything not in the catalog, added on the spot
  // (no code edit needed). Specific to this agreement, not saved to the catalog.
  const [customItems, setCustomItems] = useState<{ id: string; name: string; price: number; qty: number; ai?: boolean }[]>([]);
  const [ciName, setCiName] = useState("");
  const [ciPrice, setCiPrice] = useState("");

  // AGR-AI-BUILD — "describe the plan" in plain English; the AI assembles it
  // from the price sheet + pricebook, the dispatcher reviews before selling.
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiExplain, setAiExplain] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  // Parts the AI named that didn't land on exactly one pricebook item — the
  // dispatcher picks (propose-and-confirm; never silently guess a part).
  const [unresolved, setUnresolved] = useState<{ query: string; qty: number; matches: PricebookItem[] }[]>([]);

  async function searchPricebook(query: string): Promise<PricebookItem[]> {
    // PRICEBOOK-SEARCH-01: the one pricebook search — every word, sizes, dashes, best first.
    if (!query.trim()) return [];
    return (await searchBook(query, 8)) as PricebookItem[];
  }

  async function describePlan() {
    const text = aiText.trim();
    if (!text || aiBusy) return;
    setAiBusy(true); setAiError(null); setAiExplain(null); setUnresolved([]);
    try {
      const { data, error } = await supabase.functions.invoke("build-plan", {
        body: { question: text, basePlans: bases.map((b) => ({ id: b.id, name: b.name })), addons: addons.map((a) => ({ id: a.id, name: a.name })), terms },
      });
      if (error) throw error;
      const spec = data as { term: number; base_id: string | null; addon_ids: string[]; second_system: boolean; items: { query: string; qty: number; kind: "part" | "service" }[]; explanation: string; error?: string };
      // Apply everything we DID understand — never discard the term/items just
      // because the AI couldn't pin one field (e.g. an unclear base plan).
      if (terms.includes(spec.term as Term)) setTerm(spec.term as Term);
      const baseOk = !!spec.base_id && bases.some((b) => b.id === spec.base_id);
      if (baseOk) setBaseId(spec.base_id as string);
      setAddonIds(new Set((spec.addon_ids ?? []).filter((id) => addons.some((a) => a.id === id))));
      setSecondSystem(!!spec.second_system);
      const adds: { id: string; name: string; price: number; qty: number; ai?: boolean }[] = [];
      const stuck: { query: string; qty: number; matches: PricebookItem[] }[] = [];
      for (const it of (spec.items ?? [])) {
        // Services + included humidifier pads = covered ($0); only real billable parts hit the pricebook.
        if (it.kind === "service" || isIncludedPad(it.query)) { adds.push({ id: crypto.randomUUID(), name: `${it.query} (covered)`, price: 0, qty: it.qty, ai: true }); continue; }
        const matches = await searchPricebook(it.query);
        if (matches.length === 1) {
          const m = matches[0];
          adds.push({ id: crypto.randomUUID(), name: m.description || m.part_number || it.query, price: Number(m.base_price) || 0, qty: it.qty, ai: true });
        } else { stuck.push({ query: it.query, qty: it.qty, matches }); }
      }
      // A re-build REVISES, not appends: drop the previous AI-added lines (keep
      // anything the dispatcher added by hand), then add this build's lines.
      setCustomItems((arr) => [...arr.filter((c) => !c.ai), ...adds]);
      setUnresolved(stuck);
      // Explanation when we got a base; a soft nudge (not a blocking error) when we didn't.
      if (baseOk) {
        setAiExplain(spec.explanation);
        if (spec.error) setAiError(spec.error);
        // Fully resolved build → jump to Review so the dispatcher sees the
        // assembled plan; anything unresolved keeps them here to pick parts.
        if (stuck.length === 0) setStep(3);
      }
      else setAiError(spec.error || "I filled in everything else — pick the base plan below to finish.");
    } catch { setAiError("Couldn't reach the plan-builder AI — try again."); }
    setAiBusy(false);
  }

  function resolvePick(idx: number, m: PricebookItem) {
    const u = unresolved[idx];
    setCustomItems((arr) => [...arr, { id: crypto.randomUUID(), name: m.description || m.part_number || u.query, price: Number(m.base_price) || 0, qty: u.qty, ai: true }]);
    setUnresolved((arr) => arr.filter((_, i) => i !== idx));
  }

  const base = bases.find((b) => b.id === baseId) ?? bases[0] ?? null;
  const tk = TERM_KEY[term];
  const priceOf = (p: PlanPrice) => Number((p[tk] as number | null) ?? 0);

  const basePrice = base ? priceOf(base) : 0;
  const chosenAddons = addons.filter((a) => addonIds.has(a.id));
  const addonTotal = chosenAddons.reduce((s, a) => s + priceOf(a), 0);
  const secondTotal = secondSystem && base ? basePrice * (1 - secondDisc) : 0;
  const chosenItems = items.filter((it) => (itemQty[it.id] ?? 0) > 0);
  const itemTotal = chosenItems.reduce((s, it) => s + (it.flat ?? 0) * (itemQty[it.id] ?? 0), 0);
  const customTotal = customItems.reduce((s, ci) => s + ci.price * ci.qty, 0);
  const gross = basePrice + addonTotal + secondTotal + itemTotal + customTotal;
  // PLAN-CREDIT-14: a visit paid in full in the last 14 days earns its would-have-been
  // member savings off this plan (capped at the plan price). On by default when it
  // exists; the office can take it off or change the number.
  const { data: visitCredit } = usePlanVisitCredit(custno, term);
  const [creditOn, setCreditOn] = useState(true);
  // The edit is kept as typed (a string) so "101." and "101.2" survive re-renders; null = the computed credit.
  const [creditEdit, setCreditEdit] = useState<string | null>(null);
  const creditWanted = visitCredit ? (creditEdit === null ? visitCredit.credit : (Number(creditEdit) || 0)) : 0;
  const creditApplied = visitCredit && creditOn ? Math.round(Math.min(Math.max(0, creditWanted), gross) * 100) / 100 : 0;
  const total = Math.max(0, Math.round((gross - creditApplied) * 100) / 100);
  // Repair benefit derives from the membership tiers (the single source the
  // estimate engine also reads); fall back to the legacy config blob only if
  // no tiers are configured, so nothing breaks on an empty table.
  const repair = (discountTiers && discountTiers.length)
    ? repairPctForTerm(discountTiers, term)
    : (repairDisc[String(term)] ?? 0);

  // Coverage window: a sold plan runs term years from the start date.
  const expireDate = (() => {
    if (!startDate) return null;
    const d = new Date(startDate + "T00:00:00");
    d.setFullYear(d.getFullYear() + term);
    return d.toISOString().slice(0, 10);
  })();

  // mode "sold" = the plan is SOLD through its own invoice: written as accepted,
  // linked to the invoice, ACTIVE when the invoice is paid (PLAN-SALE-INVOICE —
  // no plan can be active with nothing collected). "quote" = priced only.
  // Both Pegasus-native, never touches ESC (DEC-22).
  const creditNote = creditApplied > 0 && visitCredit
    ? `Recent visit credit — ${fmtShort(visitCredit.date)} invoice ${visitCredit.invoice} (${money(visitCredit.total)} paid in full): member diagnostic ${money(visitCredit.diag_savings)} + ${Math.round(visitCredit.pct * 100)}% repair ${money(visitCredit.repair_savings)}`
    : null;
  const itemLinesForSale = () => [
    ...chosenItems.map((it) => ({ id: it.id, name: it.name, qty: itemQty[it.id], price: it.flat, custom: false })),
    ...customItems.map((ci) => ({ id: ci.id, name: ci.name, qty: ci.qty, price: ci.price, custom: true })),
  ];
  const save = useMutation({
    mutationFn: async (mode: "quote" | "sold") => {
      const { data: u } = await supabase.auth.getUser();
      let newSaleId: string | null = null;
      if (mode === "sold") {
        const invoiceId = await createPlanInvoice({
          custno, locno: locno ?? null, baseName: base?.name ?? null, term, total, creditNote,
          extras: [
            ...chosenAddons.map((a) => a.name),
            ...(secondSystem ? ["2nd system"] : []),
            ...chosenItems.map((it) => `${it.name}${(itemQty[it.id] ?? 1) > 1 ? ` ×${itemQty[it.id]}` : ""}`),
            ...customItems.map((ci) => `${ci.name}${ci.qty > 1 ? ` ×${ci.qty}` : ""}`),
          ],
        });
        setSaleInvoiceId(invoiceId);
        newSaleId = await sellPlan.mutateAsync({
          custno, locno: locno ?? null, term, baseId: base?.id ?? null, baseName: base?.name ?? null, total,
          invoiceId, via: "plan_builder", carryOver,
          addonIds: chosenAddons.map((a) => a.id), itemLines: itemLinesForSale(), secondSystem,
          credit: creditApplied > 0 && visitCredit ? { amount: creditApplied, invoice: visitCredit.invoice, note: creditNote } : null,
        });
        // The office's chosen start date (defaults to the renewal rule) rides on the
        // accepted row; activation honours it. Best-effort — the sale is already in.
        if (startDate && newSaleId) {
          try { await supabase.from("pegasus_plan_sale").update({ start_date: startDate }).eq("id", newSaleId).eq("status", "accepted"); } catch (e) { console.error("start date not saved:", e); }
        }
      } else {
        const { data: saleRow, error } = await supabase.from("pegasus_plan_sale").insert({
          custno, locno: locno ?? null, term, base_id: base?.id ?? null, base_name: base?.name ?? null,
          addon_ids: chosenAddons.map((a) => a.id),
          item_lines: itemLinesForSale(),
          second_system: secondSystem, total, status: "quote",
          credit_amount: creditApplied,
          credit_invoice: creditApplied > 0 && visitCredit ? visitCredit.invoice : null,
          credit_note: creditNote,
          sold_by: u?.user?.email ?? null,
        }).select("id").single();
        if (error) throw error;
        newSaleId = saleRow ? (saleRow as { id: string }).id : null;
      }
      // MEM-SALE-02: the humidifier goes on the home's plan record (count +
      // the exact pad when the equipment tells us). Best-effort, like the
      // visits — the sale never fails over a record line.
      if (mode === "sold" && humiOn && humiCount > 0) {
        try {
          const have = existingAccessories ?? [];
          const humiRow = have.find((p) => /^humidifier/i.test(p.label));
          if (humiRow) {
            if (humiRow.qty !== humiCount) await supabase.from("pegasus_plan_accessory").update({ qty: humiCount }).eq("id", humiRow.id);
          } else {
            await supabase.from("pegasus_plan_accessory").insert({
              custno, locno: locno ?? null, item_id: null, label: "Humidifier",
              qty: humiCount, kind: "humidifier", source: "plan_sale", created_by: u?.user?.email ?? null,
            });
          }
          if (detectedPad && !have.some((p) => p.label.toUpperCase() === detectedPad)) {
            await supabase.from("pegasus_plan_accessory").insert({
              custno, locno: locno ?? null, item_id: null, label: detectedPad,
              qty: humiCount, kind: "humidifier", source: "plan_sale", created_by: u?.user?.email ?? null,
            });
          }
        } catch (e) { console.error("humidifier record failed:", e); }
      }
      return newSaleId;
    },
    onSuccess: (newId, mode) => {
      qc.invalidateQueries({ queryKey: ["plan_sales", custno] });
      qc.invalidateQueries({ queryKey: ["pegasus_agreements", custno] });
      qc.invalidateQueries({ queryKey: ["plan_offers"] });
      qc.invalidateQueries({ queryKey: ["customer_proposals"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["tuneups_due"] });
      qc.invalidateQueries({ queryKey: ["plan_accessories", custno, locno ?? null] });
      // The plan becomes active when its invoice is paid — every open invoice
      // builder (office or truck) re-derives the repair discount then.
      qc.invalidateQueries({ queryKey: ["member_discount"] });
      qc.invalidateQueries({ queryKey: ["membership_status"] }); qc.invalidateQueries({ queryKey: ["quote_membership_status"] });
      qc.invalidateQueries({ queryKey: ["membership_card"] });
      qc.invalidateQueries({ queryKey: ["season_visits"] });
      setSaleId(newId ?? null);
      setSoldMode(mode === "sold");
      setSaved(true);
    },
  });

  // Welcome receipt — comms-gated (send-email enforces comms_live), so nothing
  // reaches a real customer until messaging is switched on at rollout.
  const sendWelcome = useMutation({
    mutationFn: async () => {
      const [{ data: cust }, { data: locs }] = await Promise.all([
        supabase.from("mirror_customer").select("firstname, fullname").eq("custno", custno).maybeSingle(),
        supabase.from("mirror_location").select("email").eq("custno", custno).order("locno").limit(10),
      ]);
      const email = ((locs ?? []) as { email: string | null }[]).map((r) => r.email).find((e) => e && e.trim()) ?? null;
      if (!email) throw new Error("no_email");
      const c = cust as { firstname?: string | null; fullname?: string | null } | null;
      const name = (c?.firstname?.trim() || c?.fullname?.trim()?.split(" ")[0]) || null;
      const covered = [
        base?.name ?? null,
        ...chosenAddons.map((a) => a.name),
        ...(secondSystem ? ["2nd system"] : []),
        ...chosenItems.map((it) => it.name),
        ...customItems.map((ci) => ci.name),
      ].filter(Boolean) as string[];
      const html = welcomeEmail({
        name, planName: base?.name ?? "Plus Plan", termLabel: TERM_LABEL[term],
        start: startDate, expire: expireDate ?? "", covered, repairPct: repair, tripCharge,
      });
      const { data: sd, error } = await supabase.functions.invoke("send-email", {
        body: { to: [email], subject: "Welcome to the Sedgwick Heating Plus Plan", html, custno, message_type: "plan-welcome" },
      });
      if (error) throw error;
      return { email, res: sd as { ok?: boolean } | null };
    },
    onSuccess: ({ email, res }) =>
      setWelcomeMsg(res?.ok
        ? { ok: true, text: `Welcome email sent to ${email}.` }
        : { ok: false, text: "Held — customer messaging is in testing; it sends once that's switched on." }),
    onError: (e: unknown) =>
      setWelcomeMsg({ ok: false, text: (e as Error)?.message === "no_email" ? "No email on file for this customer." : "Couldn't send — try again." }),
  });

  const qty = (id: string, d: number) => setItemQty((m) => ({ ...m, [id]: Math.max(0, (m[id] ?? 0) + d) }));

  const addCustom = () => {
    const price = Number(ciPrice);
    if (ciName.trim() === "" || !Number.isFinite(price) || price < 0) return;
    setCustomItems((xs) => [...xs, { id: crypto.randomUUID(), name: ciName.trim(), price, qty: 1 }]);
    setCiName("");
    setCiPrice("");
  };

  const visitsPerYr = (b: PlanPrice) => normalizeTemplate(b.tuneups).length;
  const STEPS: { n: 1 | 2 | 3; label: string }[] = [
    { n: 1, label: "Plan" },
    { n: 2, label: "Extras" },
    { n: 3, label: "Review & Sell" },
  ];

  return (
    <Modal open onClose={onClose} title="Price & Build a Service Agreement" size="xl">
      {isLoading ? (
        <div className="py-10 text-center text-sm text-slate-400">Loading pricing…</div>
      ) : saved ? (
        <div className="py-8 text-center">
          <Check className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
          <h3 className="text-lg font-bold text-emerald-700">
            {soldMode ? "Agreement sold · invoice ready" : "Quote saved"} — {money(total)}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {TERM_LABEL[term]} · {base?.name}.{" "}
            {soldMode
              ? `Activates when the invoice is paid — then it runs through ${fmtDate(expireDate)}.`
              : "Saved as a quote — sell it when the customer's ready."}
          </p>
          {soldMode ? (
            <div className="mt-5 flex flex-col items-center gap-2">
              <div className="flex items-center gap-2">
                {/* PLAN-SALE-INVOICE: payment is the activation — Collect lives on the invoice. */}
                {saleInvoiceId && (
                  <Button onClick={() => { onClose(); navigate(`/invoices/${saleInvoiceId}`); }}>
                    <Receipt className="h-4 w-4" /> Collect payment
                  </Button>
                )}
                <Button
                  variant="secondary"
                  disabled={sendWelcome.isPending || (welcomeMsg?.ok ?? false)}
                  onClick={() => sendWelcome.mutate()}
                >
                  <Mail className="h-4 w-4" />
                  {sendWelcome.isPending ? "Sending…" : welcomeMsg?.ok ? "Welcome email sent" : "Send welcome email"}
                </Button>
                <Button variant="secondary" onClick={onClose}>Done</Button>
              </div>
              {welcomeMsg && (
                <p className={cn("text-xs", welcomeMsg.ok ? "text-emerald-600" : "text-slate-400")}>{welcomeMsg.text}</p>
              )}
              <p className="text-xs text-slate-400">Until it is paid the plan shows as Accepted · Unpaid on the Proposals card and under Memberships → Offers; the tune-ups are laid out the moment it is paid.</p>
              {/* MEM-SALE-02: "they want to schedule their first tune-up" —
                  the term's checks, bookable right here while they're on the
                  phone (they appear once the invoice is paid). */}
              {saleId && <PostSaleVisits saleId={saleId} custno={custno} />}
            </div>
          ) : (
            <Button className="mt-5" onClick={onClose}>Done</Button>
          )}
        </div>
      ) : (
        <div>
          {/* Step rail + the always-visible running total */}
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              {STEPS.map((st, i) => (
                <span key={st.n} className="flex items-center gap-1.5">
                  {i > 0 && <span className="h-px w-4 bg-slate-200" />}
                  <button
                    type="button"
                    onClick={() => setStep(st.n)}
                    disabled={st.n !== 1 && !base}
                    className={cn(
                      "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors",
                      step === st.n
                        ? "bg-brand-700 text-white shadow-sm"
                        : step > st.n
                          ? "bg-brand-50 text-brand-700 hover:bg-brand-100"
                          : "bg-slate-100 text-slate-500 hover:text-slate-700 disabled:opacity-50",
                    )}
                  >
                    <span className={cn(
                      "flex h-4.5 w-4.5 items-center justify-center rounded-full text-[11px] font-bold",
                      step === st.n ? "bg-white/20" : step > st.n ? "bg-brand-200/60" : "bg-white",
                    )}>
                      {step > st.n ? <Check className="h-3 w-3" /> : st.n}
                    </span>
                    {st.label}
                  </button>
                </span>
              ))}
            </div>
            <div className="text-right leading-tight">
              <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{TERM_LABEL[term]} plan</div>
              <div className="text-2xl font-extrabold tabular-nums text-slate-900">{money(total)}</div>
            </div>
          </div>

          {/* ── STEP 1 · PLAN ─────────────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-5">
              {/* AI describe — one quiet line until wanted */}
              {!aiOpen ? (
                <button
                  type="button"
                  onClick={() => setAiOpen(true)}
                  className="flex w-full items-center gap-2 rounded-xl border border-dashed border-violet-300 bg-violet-50/40 px-4 py-2.5 text-left transition-colors hover:bg-violet-50"
                >
                  <Sparkles className="h-4 w-4 shrink-0 text-violet-500" />
                  <span className="text-sm font-medium text-violet-800">Or describe the plan in plain English — Pegasus builds it for you</span>
                </button>
              ) : (
                <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/70 via-white to-white p-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-[13px] font-bold text-violet-700">
                      <Sparkles className="h-4 w-4" /> Describe the Plan
                      <InfoTip label="How Describe the Plan works">Pegasus fills the plan from your price sheet and pricebook. You review it before selling.</InfoTip>
                    </div>
                    <button type="button" onClick={() => setAiOpen(false)} className="text-xs font-medium text-slate-400 hover:text-slate-600">Close</button>
                  </div>
                  <div className="flex items-start gap-2">
                    <textarea value={aiText} onChange={(e) => setAiText(e.target.value)} rows={2} autoFocus
                      placeholder='e.g. "PLUS, 3 years, two systems, Aprilaire filters twice a year, a 16x25x1 filter, annual water heater flush"'
                      className="min-w-0 flex-1 resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20" />
                    <button type="button" onClick={describePlan} disabled={aiBusy || !aiText.trim()}
                      className="shrink-0 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:bg-violet-300">
                      {aiBusy ? "Building…" : "Build it"}
                    </button>
                  </div>
                  {aiError && <p className="mt-1.5 text-xs font-medium text-amber-600">{aiError}</p>}
                  {aiExplain && <p className="mt-1.5 rounded-md bg-violet-50 px-2.5 py-1.5 text-xs text-violet-800"><strong>Built:</strong> {aiExplain}</p>}
                  {unresolved.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {unresolved.map((u, i) => (
                        <div key={i} className="rounded-md border border-amber-200 bg-amber-50/50 p-2.5">
                          <div className="mb-1 text-xs font-semibold text-amber-800">Pick the part for &ldquo;{u.query}&rdquo;{u.qty > 1 ? ` ×${u.qty}` : ""}:</div>
                          {u.matches.length === 0 ? (
                            <div className="text-xs text-slate-500">No pricebook match — add it as a custom line in Extras.</div>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {u.matches.map((m) => (
                                <button key={m.id} type="button" onClick={() => resolvePick(i, m)}
                                  className="rounded border border-slate-200 bg-white px-2 py-1 text-left text-xs text-slate-700 transition-colors hover:border-violet-300 hover:bg-violet-50">
                                  {(m.description || m.part_number || "").slice(0, 44)} · <span className="font-semibold tabular-nums">{money(Number(m.base_price) || 0)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Term — the price on every card recomputes as this changes */}
              <div>
                <div className="mb-2 text-[13px] font-semibold uppercase tracking-wider text-slate-500">How long?</div>
                <div className="flex flex-wrap gap-1.5">
                  {terms.map((t) => (
                    <button key={t} type="button" onClick={() => setTerm(t)}
                      className={cn(
                        "rounded-lg border px-4 py-2 text-sm font-semibold transition-colors",
                        term === t ? "border-brand-500 bg-brand-50 text-brand-800" : "border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900",
                      )}>
                      {TERM_LABEL[t]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Base plans as tier cards (the Run a Call pattern) */}
              <div>
                <div className="mb-2 text-[13px] font-semibold uppercase tracking-wider text-slate-500">Pick the plan</div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {bases.map((b) => {
                    const on = base?.id === b.id;
                    const vpy = visitsPerYr(b);
                    const price = priceOf(b);
                    return (
                      <button key={b.id} type="button" onClick={() => setBaseId(b.id)}
                        className={cn(
                          "relative rounded-2xl border-2 p-4 text-left transition-all",
                          on ? "border-brand-500 bg-brand-50/60 shadow-e2" : "border-slate-200 bg-white hover:border-slate-300",
                        )}>
                        {on && (
                          <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <div className={cn("text-[15px] font-bold", on ? "text-brand-900" : "text-slate-800")}>{b.name}</div>
                        <div className="mt-2 text-3xl font-extrabold tabular-nums tracking-tight text-slate-900">{money(price)}</div>
                        {term > 1 && price > 0 && (
                          <div className="text-xs text-slate-500">≈ {money(price / term)} / year</div>
                        )}
                        <div className="mt-2.5 flex items-center gap-1.5 text-[13px] text-slate-600">
                          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                          {vpy} tune-up{vpy === 1 ? "" : "s"} / year
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* 2nd system */}
                <button type="button" onClick={() => setSecondSystem((v) => !v)}
                  className={cn(
                    "mt-3 flex w-full items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left transition-colors",
                    secondSystem ? "border-emerald-400 bg-emerald-50" : "border-dashed border-slate-300 hover:border-slate-400",
                  )}>
                  <span className="flex items-center gap-2.5 text-sm">
                    <span className={cn("flex h-5 w-5 items-center justify-center rounded border", secondSystem ? "border-emerald-600 bg-emerald-600" : "border-slate-300")}>{secondSystem && <Check className="h-3.5 w-3.5 text-white" />}</span>
                    <span className={cn(secondSystem ? "font-medium text-emerald-800" : "text-slate-700")}>Cover a 2nd system <span className="text-emerald-600">({pct(secondDisc)} off the plan price)</span></span>
                  </span>
                  {secondSystem && <span className="shrink-0 text-sm font-semibold tabular-nums text-emerald-700">+{money(secondTotal)}</span>}
                </button>
              </div>

              {/* Every plan's member perks, once — not repeated on each card */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-emerald-50/70 px-4 py-2.5 text-[13px] font-medium text-emerald-800">
                <ShieldCheck className="h-4 w-4 shrink-0" />
                Every plan: {repair > 0 ? `${pct(repair)} off repairs` : "member repair discount"} · {money(tripCharge)} trip charge · priority scheduling
              </div>
            </div>
          )}

          {/* ── STEP 2 · EXTRAS ───────────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <div className="mb-2 text-[13px] font-semibold uppercase tracking-wider text-slate-500">Add-ons</div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {addons.map((a) => {
                    const on = addonIds.has(a.id);
                    return (
                      <button key={a.id} type="button"
                        onClick={() => setAddonIds((prev) => { const n = new Set(prev); if (n.has(a.id)) { n.delete(a.id); } else { n.add(a.id); } return n; })}
                        className={cn("flex items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left transition-colors", on ? "border-brand-500 bg-brand-50" : "border-slate-200 hover:border-slate-300")}>
                        <span className="flex items-center gap-2.5 text-sm">
                          <span className={cn("flex h-5 w-5 items-center justify-center rounded border", on ? "border-brand-600 bg-brand-600" : "border-slate-300")}>{on && <Check className="h-3.5 w-3.5 text-white" />}</span>
                          <span className={cn(on ? "font-medium text-brand-800" : "text-slate-700")}>{a.name}</span>
                        </span>
                        <span className="shrink-0 text-[13px] font-semibold tabular-nums text-slate-500">+{money(priceOf(a))}</span>
                      </button>
                    );
                  })}
                  {addons.length === 0 && <p className="text-sm text-slate-400">No add-ons in the catalog.</p>}
                </div>
              </div>

              {/* MEM-SALE-02 — the home itself, right in the sale: the
                  humidifier record (auto-detected, confirmed here) and the
                  covered-equipment picker that used to hide in Manage plan. */}
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wider text-slate-500">
                  The Home
                  <InfoTip label="Why this is here">What we record here follows the plan: the crew sees the humidifier (and its pad) on every tune-up, and unchecked equipment stays off the plan. None of it changes the price.</InfoTip>
                </div>
                <div className={cn("rounded-xl border px-4 py-3 transition-colors", humiOn ? "border-sky-300 bg-sky-50/40" : "border-slate-200")}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => { setHumiTouched(true); setHumiOn((v) => !v); }}
                      className="flex min-w-0 items-center gap-2.5 text-left text-sm"
                    >
                      <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded border", humiOn ? "border-sky-600 bg-sky-600" : "border-slate-300")}>
                        {humiOn && <Check className="h-3.5 w-3.5 text-white" />}
                      </span>
                      <Droplets className={cn("h-4 w-4 shrink-0", humiOn ? "text-sky-600" : "text-slate-400")} />
                      <span className={cn(humiOn ? "font-medium text-sky-900" : "text-slate-700")}>Humidifier in the home</span>
                      {detectedHumis.length > 0 && (
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700">
                          found {detectedHumis.length} on their equipment
                        </span>
                      )}
                    </button>
                    {humiOn && (
                      <span className="flex shrink-0 items-center gap-2">
                        <button type="button" onClick={() => { setHumiTouched(true); setHumiCount((c) => Math.max(1, c - 1)); }} className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"><Minus className="h-4 w-4" /></button>
                        <span className="w-5 text-center text-sm font-semibold tabular-nums">{humiCount}</span>
                        <button type="button" onClick={() => { setHumiTouched(true); setHumiCount((c) => c + 1); }} className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"><Plus className="h-4 w-4" /></button>
                      </span>
                    )}
                  </div>
                  {humiOn && (
                    <p className="mt-1.5 pl-7 text-xs text-slate-500">
                      Recorded so every tune-up services it — {detectedPad ? `pad ${detectedPad}` : "the pad"} is included with the plan, no charge.
                    </p>
                  )}
                </div>
                <div className="mt-3 rounded-xl border border-slate-200 px-4 py-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Covered Equipment</div>
                  <CoveredEquipment custno={custno} locno={locno ?? null} />
                </div>
              </div>

              <div>
                <div className="mb-2 text-[13px] font-semibold uppercase tracking-wider text-slate-500">Additional items</div>
                <div className="space-y-2">
                  {items.map((it) => {
                    const q = itemQty[it.id] ?? 0;
                    return (
                      <div key={it.id} className={cn("flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5", q > 0 ? "border-brand-300 bg-brand-50/40" : "border-slate-200")}>
                        <span className="text-sm text-slate-700">{it.name} <span className="text-xs text-slate-400">{money(it.flat ?? 0)}</span></span>
                        <span className="flex items-center gap-2">
                          <button type="button" onClick={() => qty(it.id, -1)} disabled={q === 0} className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:opacity-40"><Minus className="h-4 w-4" /></button>
                          <span className="w-5 text-center text-sm font-semibold tabular-nums">{q}</span>
                          <button type="button" onClick={() => qty(it.id, 1)} className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"><Plus className="h-4 w-4" /></button>
                        </span>
                      </div>
                    );
                  })}
                </div>

                {customItems.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {customItems.map((ci) => (
                      <div key={ci.id} className="flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50/40 px-4 py-2.5">
                        <span className="min-w-0 truncate text-sm text-slate-700">{ci.name} <span className="text-xs text-slate-400">{money(ci.price)}</span></span>
                        <span className="flex items-center gap-2">
                          <button type="button" onClick={() => setCustomItems((xs) => xs.map((x) => (x.id === ci.id ? { ...x, qty: Math.max(1, x.qty - 1) } : x)))} className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"><Minus className="h-4 w-4" /></button>
                          <span className="w-5 text-center text-sm font-semibold tabular-nums">{ci.qty}</span>
                          <button type="button" onClick={() => setCustomItems((xs) => xs.map((x) => (x.id === ci.id ? { ...x, qty: x.qty + 1 } : x)))} className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50"><Plus className="h-4 w-4" /></button>
                          <button type="button" onClick={() => setCustomItems((xs) => xs.filter((x) => x.id !== ci.id))} title="Remove" className="flex h-7 w-7 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-600"><X className="h-4 w-4" /></button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-2 flex items-center gap-2">
                  <input
                    value={ciName}
                    onChange={(e) => setCiName(e.target.value)}
                    placeholder="Add a custom item…"
                    onKeyDown={(e) => { if (e.key === "Enter") addCustom(); }}
                    className="min-w-0 flex-1 rounded-xl border border-dashed border-slate-300 px-4 py-2.5 text-sm focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                  <div className="flex items-center rounded-xl border border-dashed border-slate-300 px-2.5">
                    <span className="text-sm text-slate-400">$</span>
                    <input
                      type="number"
                      value={ciPrice}
                      onChange={(e) => setCiPrice(e.target.value)}
                      placeholder="0"
                      onKeyDown={(e) => { if (e.key === "Enter") addCustom(); }}
                      className="w-20 bg-transparent py-2.5 text-right text-sm focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addCustom}
                    disabled={ciName.trim() === "" || ciPrice.trim() === "" || Number(ciPrice) < 0}
                    className="shrink-0 rounded-xl bg-slate-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-300"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3 · REVIEW & SELL ────────────────────────────────── */}
          {step === 3 && (
            <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="mb-2 text-[13px] font-semibold uppercase tracking-wider text-slate-500">What they're getting</div>
                  <ul className="space-y-1.5 text-sm text-slate-700">
                    {base && <li className="flex justify-between gap-2"><span className="truncate font-medium">{base.name} · {TERM_LABEL[term]}</span><span className="tabular-nums">{money(basePrice)}</span></li>}
                    {chosenAddons.map((a) => <li key={a.id} className="flex justify-between gap-2"><span className="truncate">+ {a.name}</span><span className="tabular-nums">{money(priceOf(a))}</span></li>)}
                    {secondSystem && <li className="flex justify-between gap-2 text-emerald-700"><span>+ 2nd system ({pct(secondDisc)} off)</span><span className="tabular-nums">{money(secondTotal)}</span></li>}
                    {humiOn && <li className="flex justify-between gap-2 text-sky-700"><span>+ Humidifier ×{humiCount} · {detectedPad ? `pad ${detectedPad}` : "pad"} included</span><span className="text-xs font-medium text-slate-400">no charge</span></li>}
                    {chosenItems.map((it) => <li key={it.id} className="flex justify-between gap-2"><span className="truncate">+ {it.name} ×{itemQty[it.id]}</span><span className="tabular-nums">{money((it.flat ?? 0) * (itemQty[it.id] ?? 0))}</span></li>)}
                    {customItems.map((ci) => <li key={ci.id} className="flex justify-between gap-2"><span className="truncate">+ {ci.name}{ci.qty > 1 ? ` ×${ci.qty}` : ""}</span><span className="tabular-nums">{money(ci.price * ci.qty)}</span></li>)}
                    {creditApplied > 0 && visitCredit && <li className="flex justify-between gap-2 text-emerald-700"><span className="truncate">− Recent visit credit · {fmtShort(visitCredit.date)} invoice</span><span className="tabular-nums">−{money(creditApplied)}</span></li>}
                  </ul>
                  <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 text-[13px] text-slate-600">
                    <div className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> {repair > 0 ? `${pct(repair)} off repairs` : "Member repair discount"}</div>
                    <div className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> {money(tripCharge)} trip charge · priority service</div>
                    {base && <div className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> {visitsPerYr(base)} tune-up{visitsPerYr(base) === 1 ? "" : "s"} / year, auto-created on the worklist</div>}
                  </div>
                </div>
              </div>

              <div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                  <div className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wider text-slate-500">
                    {TERM_LABEL[term]} plan
                    <InfoTip label="About this sale">Saved in Pegasus only; ESC isn&apos;t touched. Prices are set in Settings → Plan Pricing.</InfoTip>
                  </div>
                  <div className="mt-1 text-3xl font-extrabold tabular-nums text-slate-900">{money(total)}</div>
                  {visitCredit && (
                    <div className={cn("mt-3 rounded-lg border px-3 py-2 text-xs", creditOn ? "border-emerald-200 bg-emerald-50/70 text-emerald-900" : "border-slate-200 bg-white text-slate-600")}>
                      <label className="flex cursor-pointer items-start gap-2">
                        <input type="checkbox" checked={creditOn} onChange={(e) => setCreditOn(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500" />
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold">Recent Visit Credit · −{money(creditApplied || Math.min(visitCredit.credit, gross))}</span>
                          <span className="block text-[11px] leading-snug opacity-90">
                            {fmtShort(visitCredit.date)} · {money(visitCredit.total)} paid in full · as a {TERM_LABEL[term].toLowerCase()} member they'd have saved {money(visitCredit.diag_savings)} on the diagnostic{visitCredit.repair_savings > 0 ? ` and ${money(visitCredit.repair_savings)} on the repair` : ""} · good through {fmtShort(visitCredit.until)}
                          </span>
                        </span>
                      </label>
                      {creditOn && (
                        <div className="mt-1.5 flex items-center gap-2 pl-5.5">
                          <span className="text-[11px] font-medium">Credit</span>
                          <span className="relative w-24"><span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                            <input type="number" step="0.01" min="0" max={gross} value={creditEdit ?? visitCredit.credit.toFixed(2)}
                              onChange={(e) => setCreditEdit(e.target.value)}
                              onBlur={() => { if (creditEdit !== null) setCreditEdit((Number(creditEdit) || 0).toFixed(2)); }}
                              className="w-full rounded-md border border-emerald-200 bg-white py-1 pl-5 pr-2 text-right text-xs tabular-nums text-slate-800 focus:border-emerald-500 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" /></span>
                          <span className="text-[11px] text-slate-500">off the plan · never cash</span>
                        </div>
                      )}
                    </div>
                  )}
                  <label className="mt-3 block text-xs font-medium text-slate-500">
                    Start date
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => { setStartTouched(true); setStartDate(e.target.value); }}
                      className="mt-1 block w-full rounded-md border border-slate-200 px-2.5 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                    />
                    {expireDate && <span className="mt-1 block text-xs text-slate-400">Covers through {fmtDate(expireDate)}</span>}
                    {renewal?.chains_from && !startTouched && renewal.start_date === renewal.prior_expire && (
                      <span className="mt-1 block text-xs text-emerald-700">
                        Renewal — {renewal.reason} ({fmtDate(renewal.prior_expire)}). Nobody loses months for renewing early.
                      </span>
                    )}
                    {renewal?.chains_from && !startTouched && renewal.start_date !== renewal.prior_expire && (
                      <span className="mt-1 block text-xs text-amber-700">{renewal.reason} — the old plan expired {fmtDate(renewal.prior_expire)}.</span>
                    )}
                  </label>
                  {leftovers && leftovers.length > 0 && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900">
                      <div className="font-semibold">The old plan still owes {leftovers.length === 1 ? "a check" : `${leftovers.length} checks`}: {leftovers.map((l) => `${l.task_label} (${l.season})`).join(", ")}.</div>
                      <div className="mt-0.5 text-amber-800">{carryOver ? "It carries over — they get it on top of the new term's checks." : "The renewal's own check covers that season; the old one closes as superseded."}</div>
                      <label className="mt-1.5 flex cursor-pointer items-center gap-2 font-medium">
                        <input type="checkbox" checked={carryOver} onChange={(e) => setCarryOver(e.target.checked)} className="h-3.5 w-3.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                        Carry it over instead
                      </label>
                    </div>
                  )}
                  <Button className="mt-3 w-full" disabled={!base || save.isPending} onClick={() => save.mutate("sold")}>
                    <Sparkles className="h-4 w-4" /> {save.isPending ? "Saving…" : `Sell agreement · ${money(total)}`}
                  </Button>
                  <button
                    type="button"
                    disabled={!base || save.isPending}
                    onClick={() => save.mutate("quote")}
                    className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
                  >
                    Save as quote instead
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step nav */}
          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            {step > 1 ? (
              <Button variant="secondary" onClick={() => setStep((s) => (s === 3 ? 2 : 1))}>Back</Button>
            ) : (
              <span />
            )}
            {step < 3 && (
              <Button disabled={!base} onClick={() => setStep((s) => (s === 1 ? 2 : 3))}>
                {step === 1 ? "Next: extras" : "Next: review & sell"}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/** MEM-SALE-02 — right after the sale: the term's generated checks, bookable
 * on the spot (Seth 2026-10-06: "they want to schedule their first tune up
 * included on their plan... How would I do that?"). Book now materializes a
 * pending-schedule ticket (same engine as Reports -> Tune-Ups Due) and links
 * it to the check, so the visit ledger and the renewal hold both know. */
function PostSaleVisits({ saleId, custno }: { saleId: string; custno: string }) {
  const qc = useQueryClient();
  const schedule = useScheduleTuneup();
  const { data } = useQuery({
    queryKey: ["post_sale_visits", saleId],
    queryFn: async () => {
      const [{ data: visits }, { data: mc }, { data: pc }] = await Promise.all([
        supabase
          .from("agreement_visit")
          .select("id,custno,locno,plan_sale_id,task_label,covers_category,sequence_label,target_date,status,fulfilled_dispatch,term_start,term_end")
          .eq("plan_sale_id", saleId)
          .order("target_date", { ascending: true }),
        supabase.from("mirror_customer").select("fullname, firstname, lastname").eq("custno", custno).maybeSingle(),
        supabase.from("pegasus_customer").select("fullname, firstname, lastname").eq("custno", custno).maybeSingle(),
      ]);
      const c = (mc ?? pc) as { fullname?: string | null; firstname?: string | null; lastname?: string | null } | null;
      const name = c?.fullname?.trim() || `${c?.firstname ?? ""} ${c?.lastname ?? ""}`.trim() || custno;
      type VisitRow = {
        id: string; custno: string; locno: string | null; plan_sale_id: string | null;
        task_label: string; covers_category: string; sequence_label: string;
        target_date: string; status: string; fulfilled_dispatch: string | null;
        term_start: string | null; term_end: string | null;
      };
      return { visits: (visits ?? []) as VisitRow[], name };
    },
  });
  const visits = data?.visits ?? [];
  if (visits.length === 0) return null;
  const lastDate = visits.reduce((m, v) => (v.target_date > m ? v.target_date : m), "");
  const whenLabel = (iso: string) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" });
  const book = (v: (typeof visits)[number]) => {
    const o: TuneupDueRow = { ...v, is_renewal_check: v.target_date === lastDate, customer_name: data?.name ?? custno };
    schedule.mutate(o, { onSuccess: () => qc.invalidateQueries({ queryKey: ["post_sale_visits", saleId] }) });
  };
  return (
    <div className="mt-5 w-full max-w-sm rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-left">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
        <CalendarClock className="h-3.5 w-3.5" /> Their included visits
      </div>
      <ul className="space-y-2">
        {visits.map((v) => (
          <li key={v.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="min-w-0 truncate text-slate-800">
              {v.task_label}
              <span className="ml-1.5 text-xs text-slate-400">around {whenLabel(v.target_date)}</span>
            </span>
            {v.status === "open" ? (
              <button
                type="button"
                disabled={schedule.isPending}
                onClick={() => book(v)}
                className="shrink-0 rounded-md bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                Book now
              </button>
            ) : (
              <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700">On the board</span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
        Book now drops the visit into the schedule queue for the dispatcher to place — it counts against the plan the moment it completes.
      </p>
    </div>
  );
}
